/**
 * 用户答题汇总情况（近 7 天）
 */

import React from 'react';
import { useHistory } from 'react-router-dom';
import { statistic, StatisticCategory, isWechatMiniProgram, openH5InNewWebview } from 'util/index';
import styles from './AnswerSummary.module.scss';

// 近 7 天每日答题（后端 statsummary.getSummary7d 返回，时间正序）
export interface DailyItem {
    date: string;
    total: number;
    error: number;
}

export interface Props {
    // 当前用户使用的技能总数
    appNum: number;
    // 总答题个数
    questionNum: number;
    // 错误率
    errorRate: string;
    // 近 7 天每日答题数（可选，用于趋势条形图）
    daily?: DailyItem[];
}

const WEEK_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

// 把 yyyymmdd 转成星期标签，最后一项显示"今"
function dayLabel(date: string, isLast: boolean): string {
    if (isLast) return '今';
    if (!date) return '';
    // 兼容 YYYY-MM-DD（cache-aside 返回）与 YYYYMMDD
    const norm = date.replace(/-/g, '');
    if (norm.length !== 8) return '';
    const y = Number(norm.slice(0, 4));
    const m = Number(norm.slice(4, 6)) - 1;
    const d = Number(norm.slice(6, 8));
    const wd = new Date(y, m, d).getDay();
    return WEEK_LABELS[wd] || '';
}

// 错误率的轻量正向语义（避免制造焦虑）
function errorRateHint(errorRate: string): string {
    const n = parseInt(errorRate, 10);
    if (isNaN(n)) return '';
    if (n <= 10) return '不错';
    if (n <= 25) return '继续加油';
    return '多练练';
}

export function AnswerSummary(props: Props) {
    const history = useHistory();
    function showExerciseRecord() {
      if (isWechatMiniProgram()) {
        openH5InNewWebview({
          url: '/mp/record',
          jumpMethod: 'navigateTo',
        });
      } else {
        history.push('/mp/record');
      }
    }
    function clickStatistic(action: string){
        return function(){
            statistic({
                category: StatisticCategory.UserCenter,
                action: `点击-${action}`,
            });
        };
    }

    const daily = props.daily || [];
    const maxTotal = daily.reduce((m, d) => Math.max(m, d.total), 0);
    const hint = errorRateHint(props.errorRate);

    return (
        <div className={styles.wrap} onClick={showExerciseRecord}>
            <div className={styles.header}>
                <span className={styles.headerTitle}>学习概览 · <span className={styles.headerRange}>近 7 天</span></span>
                <span className={styles.headerMore}>查看详情 ›</span>
            </div>
            <div className={styles.con}>
                <dl className={styles.item} onClick={clickStatistic('使用技能个数')}>
                    <dd className={styles.itemValue}>{props.appNum || 0}</dd>
                    <dt className={styles.itemTitle}>使用技能</dt>
                </dl>
                <dl className={styles.item} onClick={clickStatistic('练习题数')}>
                    <dd className={styles.itemValue}>{props.questionNum || 0}</dd>
                    <dt className={styles.itemTitle}>练习题数</dt>
                </dl>
                <dl className={styles.item} onClick={clickStatistic('错误率')}>
                    <dd className={styles.itemValue}>{props.errorRate || '0%'}</dd>
                    <dt className={styles.itemTitle}>错误率{hint ? ` · ${hint}` : ''}</dt>
                </dl>
            </div>
            {daily.length > 0 && (
                <div className={styles.trend}>
                    <div className={styles.trendTitle}>每日答题数（近 7 天）</div>
                    <div className={styles.bars}>
                        {daily.map((d, i) => {
                            const isLast = i === daily.length - 1;
                            const h = maxTotal > 0 ? Math.round((d.total / maxTotal) * 100) : 0;
                            return (
                                <div key={d.date} className={styles.barCol}>
                                    <span className={styles.barValue}>{d.total || ''}</span>
                                    <div className={styles.barTrack}>
                                        <div
                                            className={isLast ? `${styles.bar} ${styles.barToday}` : styles.bar}
                                            style={{ height: `${d.total > 0 ? Math.max(h, 6) : 0}%` }}
                                        />
                                    </div>
                                    <span className={isLast ? `${styles.barLabel} ${styles.barLabelToday}` : styles.barLabel}>
                                        {dayLabel(d.date, isLast)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
